/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import {
  absoluteFill,
  frostGlass,
  coverButton,
  headerH,
  headboardH,
  tabsHeaderH,
  tabItemH_L,
  tabItemH_P,
  playerH,
  ctrlBarWidth,
  niceScrollbar,
} from "./classroomStyle";

import { largeLandscape } from "assets/jss/material-kit-pro-react.jsx";

const styles = (theme) => {
  const tabItem = {
    [largeLandscape]: {
      ...tabItemH_L,
    },
    ...tabItemH_P,
    width: "100%",
    position: "relative",
  };

  const iboxLabel = {
    width: "100%",
    color: "#FFFFFF",
    fontWeight: 500,
    "@media (orientation: portrait)": {
      fontSize: "2.5vh",
    },
    fontSize: "1.75vw",
    paddingLeft: "0.5rem",
    display: "flex",
    alignItems: "center",
  };

  return {
    "@global": niceScrollbar,

    headBoard: {
      gridArea: "headBoard",
      position: "relative",
      background: "inherit",
      overflow: "hidden",
      "&:before": {
        ...frostGlass["&:before"],
        filter: "blur(12px)",
        boxShadow: "inset 0 0 0 200px #525BE040",
      },
      [largeLandscape]: {
        height: headboardH,
      },
      height: ctrlBarWidth,
    },

    headBoardContent: {
      minWidth: "100%",
      minHeight: "100%",
      display: "flex",
      [largeLandscape]: {
        flexDirection: "column",
        justifyContent: "flex-end",
        "@media (min-width: 1600px)": {
          padding: "1.5rem",
        },
        padding: "1rem",
      },
      "@media (orientation: portrait)": {
        alignItems: "center",
      },
    },

    headBoardTitle: {
      [largeLandscape]: {
        fontSize: "1.8vw",
        lineHeight: "125%",
        fontWeight: 500,
        fontFamily: "Raleway",
        color: "#FFFFFF",
        zIndex: 1,
      },
      "@media (orientation: portrait)": {
        display: "none", // hide Lecture Title in portrait
      },
    },

    headBoardControl: {
      [largeLandscape]: {
        margin: "1rem 0 -0.5rem",
      },
      "@media (orientation: portrait)": {
        display: "flex",
        alignItems: "center",
        margin: "0 auto",
      },
    },

    navButton: {
      "&:hover": {
        background: theme.palette.primary.dark,
      },
      background: theme.palette.primary.main,
      color: "#FFFFFF",
    },

    likeButton: {
      position: "absolute",
      right: 0,
      top: "0.5rem",
      "&:hover": {
        color: "#FF0000",
      },
      background: "transparent",
      color: "#C00000",
      [largeLandscape]: {
        "@media (max-width: 1024px)": {
          position: "fixed",
          paddingRight: "2rem",
        },
      },
    },

    sideTabs: {
      gridArea: "tabs",
      position: "relative",
      background: "inherit",
      [largeLandscape]: {
        minHeight: `calc(100vh - ${headerH} - ${headboardH})`,
      },
      minHeight: `calc(100vh - ${headerH} - ${playerH})`,
    },

    sideTabsContent: {
      ...absoluteFill,
      background: "inherit",
      [largeLandscape]: {
        height: `calc(100vh - ${headerH} - ${headboardH})`,
      },
      height: `calc(100vh - ${headerH} - ${playerH})`,
      "&:before": {
        content: "''",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: `${theme.palette.info.dark}B0`,
      },
    },

    // background of the whole <Tabs>
    tabsRoot: {
      backgroundColor: `${theme.palette.gray.dark}E0`,
    },

    // size of each tab head icon/text
    tabRoot: {
      minWidth: "105px",
      fontWeight: 600,
    },

    // CLIPBOARD
    //
    clipboard: {
      ...tabItem,
      padding: "0.5rem 0.5rem",
      backgroundColor: "#FFFFFF",
      border: "1px 0x303030",
      borderRadius: "12px",
      boxShadow: "3px 3px #606060",
      width: "96%",
      margin: "0 2% 2%",
    },

    ibxList: {
      position: "absolute",
      width: "90%",
      height: "90%",
      top: "5%",
      left: "5%",
      overflow: "auto",
      "& > :not(:last-child)": {
        marginBottom: "1.2rem",
      },
    },

    infoBox: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#F3EDD8",
      borderColor: "#A0A0A0",
      width: "100%",
    },

    iboxLabelHdo: {
      ...iboxLabel,
      backgroundColor: theme.palette.info.main,
    },

    iboxLabelCard: {
      ...iboxLabel,
      backgroundColor: theme.palette.secondary.light,
    },

    iboxLabelTest: {
      ...iboxLabel,
      backgroundColor: theme.palette.test.main,
    },

    iboxLabelAsm: {
      ...iboxLabel,
      backgroundColor: theme.palette.warning.dark,
    },

    iboxLabelLink: {
      ...iboxLabel,
      backgroundColor: theme.palette.primary.light,
    },

    iboxTitle: {
      "@media (orientation: portrait)": {
        fontSize: "2.25vh",
      },
      fontSize: "1.4vw",
      padding: "0.5rem",
      minHeight: "4rem",
    },

    clipboardCoverButton: {
      ...coverButton,
      "&:hover": {
        background: "#FFFFFF",
      },
      margin: "3rem 1rem",
      color: theme.palette.warning.main,
      background: "#F0F0F0",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
    },

    // PROGRESS
    //
    lecPanel: {
      ...tabItem,
    },

    lecList: {
      width: "100%",
      [largeLandscape]: {
        ...tabItemH_L,
      },
      "@media (orientation: portrait)": {
        maxHeight: `calc(100vh - ${headerH} - ${playerH} - ${ctrlBarWidth} - ${tabsHeaderH} - 0.5rem)`,
      },
      top: 0,
      overflow: "auto",
      position: "absolute",
    },

    lecture: {
      "@media (orientation: portrait)": {
        fontSize: "2.2vh",
      },
      fontSize: "1.2vw",
      width: "100%",
      lineHeight: "125%",
      backgroundColor: "transparent",
      padding: "1rem 1rem 0.5rem",
    },

    lecLength: {
      color: "#A1FF00",
      "@media (orientation: portrait)": {
        fontSize: "2vh",
      },
      fontSize: "1vw",
      position: "absolute",
      right: "1rem",
    },

    lecProgress: {
      display: "flex",
      alignItems: "center",
      marginTop: "0.8rem",
    },

    progressBar1: {
      backgroundColor: "#FFFFFF30",
    },

    mobileNotes: {
      ...tabItem,
    },
  };
};

export default styles;
